class SimpleSlider extends HHBaseElement {
  variant = 'single'
  activeIndex = 0

  constructor() {
    super()
    this._isReady = true
    this._isReadyPromise = new Deferred()
  }

  connectedCallback() {
    this.scrolledToStart = false
    this.scrolledToEnd = false
    this.classList[!this.isScrollable ? 'add' : 'remove']('not-scrollable')
    this.isScrollable = false
    this.isScrolling = false
    this.variant = this.getAttribute('variant') || 'single'
    this.firstSlide = this.getAttribute('data-first-slide') || '1'
    this.navA11y = this.getAttribute('data-nav-a11y') || false
    this.$nextBtn = this.querySelectorAll('[data-slider-next]')
    this.$backBtn = this.querySelectorAll('[data-slider-back]')
    this.$status = this.querySelector('[data-slider-status]')
    this.$dots = this.querySelectorAll('[data-slider-dots] .simple-slider-dot')
    this.$removeDots = this.querySelector('[data-remove-desktop-dots]')
    this.cloneSetCounter = 1

    this._addClones = this._addClones.bind(this)
    this._handleNext = this._handleNext.bind(this)
    this._handleBack = this._handleBack.bind(this)
    this._addToIntersectionObservers =
      this._addToIntersectionObservers.bind(this)
    this._mutationWatcher = this._mutationWatcher.bind(this)
    this._handleContainerScroll = this._handleContainerScroll.bind(this)
    this._handleIntersection = this._handleIntersection.bind(this)
    this._handleADAIntersection = this._handleADAIntersection.bind(this)
    this._initCloneSet = this._initCloneSet.bind(this)
    this.classList.add(`simple-slider--${this.variant}`)
    this._handleDotClick = this._handleDotClick.bind(this)
    this._clearActiveDots = this._clearActiveDots.bind(this)
    this._updateActiveDot = this._updateActiveDot.bind(this)
    this.reInitSlider = this.reInitSlider.bind(this)
    this._findItemWithLargestNumber = this._findItemWithLargestNumber.bind(this)

    document.addEventListener('keydown', this._handleKeyDown.bind(this))

    if (this.infinite) {
      this.classList.add(`simple-slider--infinite`)
    }
    this.$itemsContainer = this.querySelector('[data-slider-items]')

    this._removeDots()
    this._updateActiveDot(0)

    this._attachListeners()

    if (this.$items.length >= 1) {
      this._applyState()
      this._handleContainerScroll()
      this._initCloneSet()

      if (this.infinite) {
        // Adding Placeholder Clones
        let addClones = this._addClones
        setTimeout(() => {
          addClones()
          addClones(false)
        }, 1000)
      }
    }

    if (this.firstSlide != '1') {
      this.scrollCarousel(this.scrollDistance * parseFloat(this.firstSlide - 1))
    }
  }

  disconnectedCallback() {
    this._removeListeners()

    document.removeEventListener('keydown', this._handleKeyDown.bind(this))
  }

  _removeDots() {
    if (this.$removeDots) {
      if (window.innerWidth < 992) {
        this.$removeDots.remove()
        this.$dots = this.querySelectorAll(
          '[data-slider-dots] .simple-slider-dot'
        )
      } else {
        this.$dots = this.querySelectorAll(
          '[data-remove-desktop-dots] .simple-slider-dot'
        )
      }
    }
  }

  renderWithBaseZero(amount) {
    return amount < 10 ? `0${amount}` : amount
  }

  reInitSlider() {
    this._removeListeners()
    this._attachListeners()
    this.activeIndex = 0
    this.scrollJumpTo(0)

    if (this.$items.length >= 1) {
      this._applyState()
      this._handleContainerScroll()
      this._initCloneSet()

      if (this.infinite) {
        // Adding Placeholder Clones
        let addClones = this._addClones
        setTimeout(() => {
          addClones()
          addClones(false)
        }, 1000)
      }
    }

    if (this.firstSlide != '1') {
      this.scrollCarousel(this.scrollDistance * parseFloat(this.firstSlide - 1))
    }
  }

  get $items() {
    return Array.from(this.querySelectorAll('[data-slider-item]')).filter(
      (item) => !item.classList.contains('hidden')
    )
  }

  set $items(val) {
    return val
  }

  get _status() {
    let currentSlide = this.renderWithBaseZero(this.activeIndex + 1)

    const finalSlide = this.renderWithBaseZero(
      this.$items.filter((item) => !item.classList.contains('is-clone')).length
    )

    return `${currentSlide} / ${finalSlide}`
  }

  get autoplayEnabled() {
    return this.getAttribute('autorotate') === 'true'
  }

  get scrollBy() {
    if (window.innerWidth < 992 && this.scrollByMobile) {
      return this.scrollByMobile
    }

    return parseFloat(this.getAttribute('scrollBy')) || 1
  }

  get scrollByMobile() {
    return parseFloat(this.getAttribute('scrollByMobile')) || null
  }

  get autoplayInterval() {
    return parseFloat(this.getAttribute('autorotate-interval')) * 1000
  }

  get loops() {
    return this.$items.length > 1 ? !!this.getAttribute('loop') : false
  }

  get infinite() {
    return this.loops && !this.$status
  }

  get scrollDistance() {
    return this.$items[0].clientWidth
  }

  get isPdpSlider() {
    return this.getAttribute('isPdpSlider') === 'true'
  }

  get carouselInnerPadding() {
    const padding = parseInt(
      getComputedStyle(this.$itemsContainer).getPropertyValue(
        '--simple-carousel-padding'
      )
    )

    return (isNaN(padding) ? 0 : padding) + this.infinite
      ? this.scrollDistance
      : 0
  }

  _initCloneSet() {
    this.$items.forEach((item) => {
      item.classList.add(`clone-set-${this.cloneSetCounter}`)
    })
  }

  _handleButtonEvent(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  async _handleNext(event) {
    if (this.isScrolling) {
      return
    }

    if (!this._isReady) {
      await this._isReadyPromise.promise
    }
    this._isReady = false

    this._handleButtonEvent(event)

    if (this.scrolledToEnd) {
      if (this.infinite) {
        this._addClones(false)
      } else if (this.loops) {
        this._updateActiveDot(0)
        return this.scrollTo(0)
      } else {
        return
      }
    }

    let currentSlideIndex = parseFloat(this.activeIndex + 1)

    if (this.isPdpSlider) {
      this.scrollCarousel(this.scrollDistance)
      let activeSlide = this.$items[currentSlideIndex]
      let activeSlidePreviewUrl =
        activeSlide.querySelector('button').dataset.previewImageUrl

      let selectedVariantWithoutClones = this.$items.filter(
        (item) => !item.classList.contains('is-clone')
      )

      let dotIndex = selectedVariantWithoutClones.findIndex(
        (item) =>
          item.querySelector('button').dataset.previewImageUrl ===
          activeSlidePreviewUrl
      )

      this._updateActiveDot(dotIndex)

      if (this.infinite) {
        clearTimeout(this.removeNextClonesTimer)
        this.removeNextClonesTimer = setTimeout(() => {
          this.removeClonesNotInView()
        }, 1000)
      }
    } else {
      if ([...this.$dots].length > 0) {
        let activeDotIndex = parseFloat(
          [...this.$dots].find((dot) => dot.classList.contains('active'))
            ?.dataset.index ?? 0
        )

        this._updateActiveDot(activeDotIndex + 1)
      }

      this.scrollCarousel(this.scrollDistance * this.scrollBy)
    }
  }

  async _handleBack(event) {
    if (this.isScrolling) {
      return
    }

    if (!this._isReady) {
      await this._isReadyPromise.promise
    }
    this._isReady = false

    this._handleButtonEvent(event)

    if (this.scrolledToStart) {
      if (this.infinite) {
        const currentElement = this.$items[0]
        this._addClones(true)
        this.scrollJumpTo(currentElement.offsetLeft)
      } else if (this.loops) {
        return this.scrollTo(
          this.$itemsContainer.scrollWidth - this.$itemsContainer.clientWidth
        )
      } else {
        return
      }
    }

    if (this.isPdpSlider) {
      let allVisibleDots = [...this.$dots].filter(
        (dot) => !dot.parentElement.classList.contains('hidden')
      )

      let activeDotIndex = allVisibleDots.findIndex((item) =>
        item.classList.contains('active')
      )

      this._updateActiveDot(
        activeDotIndex - 1 >= 0 ? activeDotIndex - 1 : allVisibleDots.length - 1
      )

      this.scrollCarousel(-1 * this.scrollDistance)

      if (this.infinite) {
        clearTimeout(this.removePrevClonesTimer)
        this.removePrevClonesTimer = setTimeout(() => {
          this.removeClonesNotInView(true)
        }, 1000)
      }
    } else {
      if ([...this.$dots].length > 0) {
        let activeDotIndex = parseFloat(
          [...this.$dots].find((dot) => dot.classList.contains('active'))
            ?.dataset.index || 1
        )
        this._updateActiveDot(activeDotIndex - 1)
      }

      let scrollByColumns = this.scrollBy
      if (this.scrolledToEnd && this.scrollBy > 1) {
        const totalCount = this.$items.filter(
          (item) => !item.classList.contains('is-clone')
        ).length
        scrollByColumns = totalCount % this.scrollBy
        scrollByColumns = scrollByColumns > 0 ?? this.scrollBy
      }

      this.scrollCarousel(this.scrollDistance * scrollByColumns * -1)
    }
  }

  _mutationWatcher() {
    this.$items = Array.from(
      this.querySelectorAll('[data-slider-item]')
    ).filter((item) => !item.classList.contains('hidden'))

    if (this.$items.length >= 1) {
      this.$items.forEach((item) => this._addToIntersectionObservers(item))
      this._handleContainerScroll()
      this._applyState()
    }
  }

  _handleContainerScroll() {
    const { scrollLeft, scrollWidth, clientWidth } = this.$itemsContainer
    this.scrolledToStart = scrollLeft <= this.carouselInnerPadding
    const scrollEndRatio =
      (scrollLeft + this.carouselInnerPadding) / (scrollWidth - clientWidth)
    this.scrolledToEnd = scrollEndRatio > 0.98
    this.isScrollable = scrollWidth > clientWidth
    // this._setupADA()
    this._applyState()
  }

  _addClones(prepend = false) {
    const clones = this.$items.map((item) => item.cloneNode(true))
    clones.forEach((item) => {
      item.classList.add('is-clone')
      item.classList.remove(`clone-set-${this.cloneSetCounter}`)
      item.classList.add(
        `clone-set-${prepend ? this.cloneSetCounter - 1 : this.cloneSetCounter + 1
        }`
      )
      this._addToIntersectionObservers(item)
    })

    if (prepend) {
      this.$itemsContainer.prepend(...clones)
    } else {
      this.$itemsContainer.append(...clones)
    }

    this.cloneSetCounter = this.cloneSetCounter + 1
  }

  _isVisible(elem, removePrevious = false) {
    const scrollLeft = this.$itemsContainer.scrollLeft
    const clientWidth = this.$itemsContainer.clientWidth
    const elementLeft = elem.offsetLeft
    if (removePrevious) {
      const offset = elementLeft - scrollLeft
      const isOutOfView = offset > clientWidth * 2

      return !isOutOfView
    }

    const offset = scrollLeft - elementLeft
    const isOutOfView = offset > clientWidth * 2
    return !isOutOfView
  }

  removeClonesNotInView(removePrevious = false) {
    Array.from(this.querySelectorAll('.is-clone'))
      .filter((item) => !this._isVisible(item, removePrevious))
      .forEach((item) => {
        item.remove()
      })
  }

  _setUpResizeObserver() {
    if ('ResizeObserver' in window) {
      // Update scrollflags when a child's size changes (e.g., a custom element becomes
      // defined or an image is loaded)
      this.resizeObserver = new ResizeObserver(() =>
        this._handleContainerScroll()
      )
      Array.from(this.children).forEach((element) => {
        this.resizeObserver.observe(element)
      })
    }
  }

  _addToIntersectionObservers(item) {
    this.activeItemObserver.observe(item)
    this.adaVisiblityObserver.observe(item)
  }

  _setItemADAVisible(item) {
    item.removeAttribute('aria-hidden')
    item.removeAttribute('tabindex')
    this._handleSlideA11y(item)
  }

  _setItemADAHidden(item) {
    item.setAttribute('aria-hidden', 'true')
    item.setAttribute('tabindex', '-1')
    this._handleSlideA11y(item, false)
  }

  _handleSlideA11y(item, visible = true) {
    const focusableElements =
      'button, .button, [href], input[type="email"], input[type="text"], select, textarea, [tabindex]:not([tabindex="-1"])'
    const focusableContent = [...item.querySelectorAll(focusableElements)]
    if (focusableContent.length > 0) {
      focusableContent.forEach((itm) => {
        if (visible) {
          itm.setAttribute('tabIndex', 0)
        } else {
          itm.setAttribute('tabIndex', -1)
        }
      })
    }
  }

  _handleADAIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this._setItemADAVisible(entry.target)
      } else {
        this._setItemADAHidden(entry.target)
      }
    })
  }

  _handleIntersection(entries) {
    let gotActiveIndex = false
    entries.forEach((entry) => {
      if (entry.isIntersecting && !gotActiveIndex) {
        const activeIndex = this.$items.indexOf(entry.target)
        if (activeIndex !== this.activeIndex) {
          this.activeIndex = activeIndex
          this._applyState()
        }
        gotActiveIndex = true
      }
    })
  }

  _attachListeners() {
    if (this.$nextBtn.length) {
      this.$nextBtn.forEach((btn) => {
        btn.addEventListener('click', this._handleNext)
      })
    }
    if (this.$backBtn.length) {
      this.$backBtn.forEach((btn) => {
        btn.addEventListener('click', this._handleBack)
      })
    }
    this.optionsObserver = new MutationObserver(this._mutationWatcher)
    this.optionsObserver.observe(this.$itemsContainer, {
      childList: true,
    })
    this._setUpResizeObserver()

    this.$itemsContainer.addEventListener('scroll', this._handleContainerScroll)
    this.activeItemObserver = new IntersectionObserver(
      this._handleIntersection,
      { root: this.$itemsContainer, threshold: 0.51 }
    )

    this.adaVisiblityObserver = new IntersectionObserver(
      this._handleADAIntersection,
      { root: this.$itemsContainer, threshold: 0.01 }
    )
    this.$items.forEach((item) => this._addToIntersectionObservers(item))

    if (this.autoplayEnabled) {
      this.setAutoplayTimer()
      this.addEventListener('mouseover', this.clearAutoplayTimer)
      this.addEventListener('mouseout', this.setAutoplayTimer)
    }

    if (this.$dots) {
      this.$dots.forEach((dot) =>
        dot.addEventListener('click', this._handleDotClick)
      )
    }

    this.addEventListener('swiped-left', this._handleNext, false)
    this.addEventListener('swiped-right', this._handleBack, false)

    this._listenersAttached = true
  }

  _removeListeners() {
    if (!this._listenersAttached) return
    if (this.$nextBtn.length) {
      this.$nextBtn.forEach((btn) => {
        btn.removeEventListener('click', this._handleNext)
      })
    }
    if (this.$backBtn.length) {
      this.$backBtn.forEach((btn) => {
        btn.removeEventListener('click', this._handleBack)
      })
    }

    this.$itemsContainer.removeEventListener('scroll', this._applyIndexState)
    this.$itemsContainer.removeEventListener(
      'scroll',
      this._handleContainerScroll
    )

    if (this.optionsObserver) this.optionsObserver.disconnect()
    if (this.activeItemObserver) this.activeItemObserver.disconnect()
    if (this.adaVisiblityObserver) this.adaVisiblityObserver.disconnect()
    if (this.resizeObserver) this.resizeObserver.disconnect()

    if (this.autoplayEnabled) {
      this.removeEventListener('mouseenter', this.clearAutoplayTimer)
      this.removeEventListener('mouseout', this.setAutoplayTimer)
    }

    if (this.$dots) {
      this.$dots.forEach((dot) =>
        dot.removeEventListener('click', this._handleDotClick)
      )
    }

    this.removeEventListener('swiped-left', this._handleNext, false)
    this.removeEventListener('swiped-right', this._handleBack, false)

    this._listenersAttached = false
  }

  _applyState() {
    // check slide status
    let atEndOfSlides = false

    if (this._status && !this.infinite) {
      let numbers = this._status.split('/')
      if (parseFloat(numbers[0]) === parseFloat(numbers[1])) {
        atEndOfSlides = true
      }

      const currentDotIndex = Number(numbers[0]) - 1
      this._updateActiveDot(currentDotIndex)
    }

    this.classList[this.scrolledToStart ? 'add' : 'remove']('first-slide')
    this.classList[this.scrolledToEnd || atEndOfSlides ? 'add' : 'remove'](
      'last-slide'
    )

    this.classList[!this.isScrollable ? 'add' : 'remove']('not-scrollable')
    if (this.$status) {
      this.$status.innerText = this._status
    }

    if (this.navA11y) {
      this._handleNavA11y()
    }

    if (this.infinite) {
      this._handleInfiniteLoopA11y()
    }
  }

  _handleInfiniteLoopA11y() {
    // Make offscreen slides inert to prevent getting stuck in infinite tabbing
    this.$items.forEach(($item, index) => {
      if (index === this.activeIndex) {
        $item.removeAttribute('inert')
      } else {
        $item.setAttribute('inert', '')
      }
    })
  }

  _handleNavA11y() {
    this._setNavA11YState(this.$backBtn)
    this._setNavA11YState(this.$nextBtn, 'last-slide')
  }

  _setNavA11YState(buttons, state = 'first-slide') {
    if (buttons.length) {
      buttons.forEach((btn) => {
        if (this.classList.contains(state)) {
          btn.setAttribute('tabIndex', -1)
          btn.setAttribute('aria-disabled', true)
        } else {
          btn.setAttribute('tabIndex', 0)
          btn.setAttribute('aria-disabled', false)
        }
      })
    }
  }

  clearAutoplayTimer() {
    try {
      clearInterval(this.slideshowTimer)
      this.slideshowTimer = null
    } catch (e) {
      console.log(e)
    }
  }

  setAutoplayTimer() {
    try {
      let interval = this.autoplayInterval
      this.slideshowTimer = setInterval(() => {
        this._handleNext()
      }, interval)
    } catch (error) {
      console.error(error)
    }
  }

  scrollCarousel(num, debounceDuration = 350) {
    this.isScrolling = true
    setTimeout(() => {
      this.isScrolling = false
    }, debounceDuration)

    this.$itemsContainer.scrollLeft += num
    setTimeout(() => {
      this._isReady = true
      this._isReadyPromise.resolve()
    }, 250)
  }

  scrollTo(num) {
    this.$itemsContainer.scrollLeft = num
  }

  scrollJumpTo(x) {
    if (!this.$itemsContainer) return

    this.$itemsContainer.style.scrollBehavior = 'unset'
    this.$itemsContainer.scrollTo(x, 0)
    this.$itemsContainer.style.scrollBehavior = ''
  }

  _clearActiveDots() {
    let dots = [...this.$dots]
    dots.map((dot) => {
      dot.classList.remove('active')
      dot.setAttribute('aria-current', false)
    })
  }

  _updateActiveDot(newDotIndex) {
    if (!this.$dots || this.$dots.length === 0) return
    this._clearActiveDots()

    const groupIndex = Math.floor(newDotIndex / this.scrollBy)
    let visibleDots = [...this.$dots].filter(
      (dot) => !dot.parentElement.classList.contains('hidden')
    )
    visibleDots[groupIndex]?.classList.add('active')
    visibleDots[groupIndex]?.setAttribute('aria-current', true)
  }

  _findItemWithLargestNumber(array) {
    let largestNumber = -Infinity
    let itemWithLargestNumber = null

    for (let i = 0; i < array.length; i++) {
      const match = array[i].match(/(\d+)$/)
      if (match) {
        const number = parseInt(match[1])
        if (number > largestNumber) {
          largestNumber = number
          itemWithLargestNumber = array[i]
        }
      }
    }

    return itemWithLargestNumber
  }

  async _handleDotClick(e) {
    if (this.isScrolling) {
      return
    }

    if (!this._isReady) {
      await this._isReadyPromise.promise
    }
    this._isReady = false

    this._handleButtonEvent(e)

    // 1. Get current dot index
    // 2. Compare to click dot index
    // 3. If click dot index is greater than current dot index, scroll right

    let allVisibleDots = [...this.$dots].filter(
      (dot) => !dot.parentElement.classList.contains('hidden')
    )

    let activeDotIndex = allVisibleDots.findIndex((item) =>
      item.classList.contains('active')
    )

    let clickedDotIndex = parseFloat(e.target.dataset.index)

    let difference = clickedDotIndex - activeDotIndex

    this._updateActiveDot(clickedDotIndex)
    if (this.isPdpSlider) {
      this.scrollCarousel(
        this.scrollDistance * difference,
        Math.abs(difference * 80) + 270
      )
    } else {
      let scrollByColumns = this.scrollBy
      let totalScrollDistance = difference * scrollByColumns
      if (difference < 0 && this.scrolledToEnd && this.scrollBy > 1) {
        const totalCount = this.$items.filter(
          (item) => !item.classList.contains('is-clone')
        ).length
        scrollByColumns = totalCount % this.scrollBy
        scrollByColumns = scrollByColumns > 0 ?? this.scrollBy
        totalScrollDistance = (difference + 1) * this.scrollBy - scrollByColumns
      }
      this.scrollCarousel(
        this.scrollDistance * totalScrollDistance,
        Math.abs(difference * 80) + 270
      )
    }
  }

  static get observedAttributes() {
    return ['variant', 'itemCount']
  }

  _clearClones() {
    let clones = [...document.querySelectorAll('.is-clone')]
    clones.forEach((clone) => clone.remove())
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Product Gallery hides/shows respective variants

    if (name === 'variant') {
      // clear clones

      this._clearClones()
      this.$items = Array.from(
        this.querySelectorAll('[data-slider-item]')
      ).filter((item) => !item.classList.contains('hidden'))

      this.scrollJumpTo(0)
    } else if (name === 'itemCount') {
      this.reInitSlider()
    }
  }

  _handleKeyDown(event) {
    if (event.keyCode === 13) {
      if (document.activeElement.hasAttribute('tabindex')) {
        document.activeElement.click()
      }
    }
  }
}

window.customElements.define('simple-slider', SimpleSlider)
